import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { SupportAdminService } from '../../../services/support/support-admin.service';
import { ToastService } from '../../../shared/services';
import {
  AdminSupportTicket,
  SupportTicketMessage,
  SupportTicketStatus,
} from '../../../shared/interfaces';
import { SupportThread } from '../../user/support/support-thread/support-thread';
import {
  categoryLabel,
  impactLabel,
  ticketCode,
} from '../../user/support/support-labels';

type InboxFilter = 'attention' | 'waiting' | 'finished' | 'all';
type ReplyStatus = 'waiting_user' | 'in_progress' | 'resolved';

const FINISHED: SupportTicketStatus[] = ['resolved', 'closed'];

@Component({
  selector: 'app-admin-support',
  imports: [DatePipe, FormsModule, SupportThread],
  templateUrl: './support.html',
  styleUrl: './support.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSupport implements OnInit {
  private readonly supportAdmin = inject(SupportAdminService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tickets = signal<AdminSupportTicket[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly filter = signal<InboxFilter>('attention');
  readonly search = signal('');
  readonly selectedId = signal<string | null>(null);

  readonly messages = signal<SupportTicketMessage[]>([]);
  readonly messagesLoading = signal(false);
  readonly replyText = signal('');
  readonly replyStatus = signal<ReplyStatus>('waiting_user');
  readonly sending = signal(false);
  readonly changingStatus = signal(false);
  readonly replyMax = 4000;

  private readonly search$ = new Subject<string>();

  readonly code = ticketCode;
  readonly categoryLabel = categoryLabel;
  readonly impactLabel = impactLabel;

  readonly statusOptions: { value: SupportTicketStatus; label: string }[] = [
    { value: 'open', label: 'Recibido' },
    { value: 'in_progress', label: 'En revisión' },
    { value: 'waiting_user', label: 'Esperando al usuario' },
    { value: 'resolved', label: 'Resuelto' },
    { value: 'closed', label: 'Cerrado' },
  ];

  readonly replyStatusOptions: { value: ReplyStatus; label: string; hint: string }[] = [
    { value: 'waiting_user', label: 'Esperar respuesta', hint: 'Le pides algo al usuario' },
    { value: 'in_progress', label: 'Seguir revisando', hint: 'Solo le cuentas cómo va' },
    { value: 'resolved', label: 'Marcar resuelto', hint: 'Con esto queda solucionado' },
  ];

  readonly filters: { value: InboxFilter; label: string }[] = [
    { value: 'attention', label: 'Por atender' },
    { value: 'waiting', label: 'Esperando al usuario' },
    { value: 'finished', label: 'Cerrados' },
    { value: 'all', label: 'Todos' },
  ];

  readonly counts = computed(() => {
    const list = this.tickets();
    return {
      attention: list.filter((t) => this.needsAttention(t)).length,
      waiting: list.filter((t) => t.status === 'waiting_user').length,
      finished: list.filter((t) => FINISHED.includes(t.status)).length,
      all: list.length,
    };
  });

  readonly visibleTickets = computed(() => {
    const list = this.tickets();
    switch (this.filter()) {
      case 'attention':
        return list.filter((t) => this.needsAttention(t));
      case 'waiting':
        return list.filter((t) => t.status === 'waiting_user');
      case 'finished':
        return list.filter((t) => FINISHED.includes(t.status));
      default:
        return list;
    }
  });

  readonly selected = computed(
    () => this.tickets().find((t) => t.supportTicketId === this.selectedId()) ?? null
  );

  readonly emptyText = computed(() => {
    if (this.search().trim()) return 'Ningún ticket coincide con la búsqueda.';
    switch (this.filter()) {
      case 'attention':
        return 'No hay tickets por atender. Los nuevos y los que el usuario respondió aparecen aquí.';
      case 'waiting':
        return 'Ningún ticket está esperando respuesta del usuario.';
      case 'finished':
        return 'Aún no hay tickets resueltos o cerrados.';
      default:
        return 'Todavía no se ha creado ningún ticket.';
    }
  });

  private readonly relativeFormatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadTickets(this.selectedId() ?? undefined));
    this.loadTickets();
  }

  /** Tickets the team owes an answer on: new, being worked on, or answered by the user */
  needsAttention(ticket: AdminSupportTicket): boolean {
    return ticket.status === 'open' || ticket.status === 'in_progress';
  }

  isNew(ticket: AdminSupportTicket): boolean {
    return ticket.status === 'open' && ticket.messageCount === 0;
  }

  userReplied(ticket: AdminSupportTicket): boolean {
    return ticket.lastMessageRole === 'user' && !FINISHED.includes(ticket.status);
  }

  statusLabel(status: SupportTicketStatus): string {
    return this.statusOptions.find((s) => s.value === status)?.label ?? status;
  }

  lastActivity(ticket: AdminSupportTicket): string {
    return this.relative(ticket.updatedAt ?? ticket.createdAt);
  }

  onSearch(value: string): void {
    this.search.set(value);
    this.search$.next(value);
  }

  select(ticket: AdminSupportTicket): void {
    if (this.selectedId() === ticket.supportTicketId) return;
    this.selectedId.set(ticket.supportTicketId);
    this.messages.set([]);
    this.replyText.set('');
    this.replyStatus.set('waiting_user');
    this.loadMessages(ticket.supportTicketId);
  }

  closeDetail(): void {
    this.selectedId.set(null);
  }

  sendReply(ticket: AdminSupportTicket): void {
    const body = this.replyText().trim();
    if (!body || this.sending()) return;
    this.sending.set(true);
    this.supportAdmin
      .reply(ticket.supportTicketId, body, this.replyStatus())
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe({
        next: () => {
          this.replyText.set('');
          this.toastService.show('Respuesta enviada. Le avisamos al usuario por correo.', 'success');
          this.loadMessages(ticket.supportTicketId);
          this.loadTickets(ticket.supportTicketId);
        },
        error: (err) => this.showError(err, 'No se pudo enviar la respuesta. Inténtalo de nuevo.'),
      });
  }

  changeStatus(ticket: AdminSupportTicket, status: SupportTicketStatus): void {
    if (ticket.status === status || this.changingStatus()) return;
    this.changingStatus.set(true);
    this.supportAdmin
      .changeStatus(ticket.supportTicketId, status)
      .pipe(finalize(() => this.changingStatus.set(false)))
      .subscribe({
        next: () => {
          this.toastService.show(`Ticket ${this.code(ticket.ticketNumber)}: ${this.statusLabel(status)}`, 'success');
          this.loadTickets(ticket.supportTicketId);
        },
        error: (err) => this.showError(err, 'No se pudo cambiar el estado. Inténtalo de nuevo.'),
      });
  }

  private loadTickets(keepSelected?: string): void {
    this.loading.set(true);
    this.supportAdmin
      .listTickets(this.search())
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loaded.set(true);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (tickets) => {
          this.tickets.set(
            tickets.map((t) => ({
              ...t,
              status: (t.status?.toLowerCase() ?? 'open') as SupportTicketStatus,
              messageCount: t.messageCount ?? 0,
            }))
          );
          if (keepSelected && !this.tickets().some((t) => t.supportTicketId === keepSelected)) {
            this.selectedId.set(null);
          }
        },
        error: (err) => this.showError(err, 'No se pudieron cargar los tickets. Recarga la página.'),
      });
  }

  private loadMessages(ticketId: string): void {
    this.messagesLoading.set(true);
    this.supportAdmin
      .listMessages(ticketId)
      .pipe(finalize(() => this.messagesLoading.set(false)))
      .subscribe({
        next: (messages) => {
          if (this.selectedId() === ticketId) this.messages.set(messages);
        },
        error: (err) => this.showError(err, 'No se pudo cargar la conversación.'),
      });
  }

  private relative(iso: string): string {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (isNaN(diffMs)) return '';
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 60) return this.relativeFormatter.format(minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return this.relativeFormatter.format(hours, 'hour');
    const days = Math.round(hours / 24);
    if (Math.abs(days) < 30) return this.relativeFormatter.format(days, 'day');
    return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private showError(err: any, fallback: string): void {
    const message = err?.error?.errors?.[0] || err?.error?.message || fallback;
    this.toastService.show(message, 'warning');
  }
}
